import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import Header from './components/Header';
import Footer from './components/Footer';
import Swap from './components/Swap';
import Liquidity from './components/Liquidity';
import Crosschain from './components/Crosschain';
import Faucet from './components/Faucet';
import MarketTicker from './components/common/MarketTicker';
import { useLanguage } from './context/LanguageContext';
import { 
  NavSwapIcon, 
  NavLiquidityIcon, 
  NavBridgeIcon, 
  NavFaucetIcon,
  ZapIcon,
  RocketIcon,
  DropsIcon,
  ChartBarIcon,
  UsersIcon,
  CoinsIcon,
  ShieldLockIcon,
  TrendingUpIcon,
  WalletIcon,
  CheckIcon,
  ErrorIcon,
  ArrowRightIcon
} from './components/common/Icons';
import Toast from './components/common/Toast';
import './App.css';

const NETWORKS: Record<string, any> = {
  huanchain_mainnet: {
    chainId: '0x22b8', // 8888 in hex
    chainIdNumber: 8888,
    chainName: 'HuanChain L2',
    nativeCurrency: {
      name: 'HuanChain',
      symbol: 'HUC',
      decimals: 18,
    },
    rpcUrls: ['https://rpc.huanchain.online'],
    blockExplorerUrls: ['https://scan.huanchain.online'],
    routerAddress: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
    factoryAddress: '0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f',
    wbnbAddress: '0x1A2B3C4D5E6F7A8B9C0D1E2F3A4B5C6D7E8F9A0B',
    tokens: {
      HUC: {
        symbol: 'HUC',
        name: 'HuanChain Native',
        address: 'HUC',
        decimals: 18,
        isNative: true,
        coinId: 99999
      },
      USDT: {
        symbol: 'USDT',
        name: 'Tether USD',
        address: '0x337610d27c682E347C9cD60BD4b3b107C9d34dDd',
        decimals: 18,
        isNative: false,
        coinId: 825
      },
      USDC: {
        symbol: 'USDC',
        name: 'USD Coin',
        address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
        decimals: 18,
        isNative: false,
        coinId: 3408
      },
      BNB: {
        symbol: 'BNB',
        name: 'Binance Coin',
        address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
        decimals: 18,
        isNative: false,
        coinId: 1839
      },
      ETH: {
        symbol: 'ETH',
        name: 'Ethereum',
        address: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8',
        decimals: 18,
        isNative: false,
        coinId: 1027
      }
    }
  },
  bsc_mainnet: {
    chainId: '0x38', // 56 in hex
    chainIdNumber: 56,
    chainName: 'BNB Chain',
    nativeCurrency: {
      name: 'BNB',
      symbol: 'BNB',
      decimals: 18,
    },
    rpcUrls: ['https://bsc-dataseed1.binance.org'],
    blockExplorerUrls: ['https://bscscan.com'],
    routerAddress: '0x10ED43C718714eb63d5aA57B78B54704E256024E', // PancakeSwap V2 Router
    factoryAddress: '0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73', // PancakeSwap V2 Factory
    wbnbAddress: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
    tokens: {
      BNB: {
        symbol: 'BNB',
        name: 'Binance Coin',
        address: 'BNB',
        decimals: 18,
        isNative: true,
        coinId: 1839
      },
      HUC: {
        symbol: 'HUC',
        name: 'HuanChain Token',
        address: '0x38B0F25A12591b65bC992b8d0b2849F30689bA4F',
        decimals: 18,
        isNative: false,
        coinId: 99999
      },
      WBNB: {
        symbol: 'WBNB',
        name: 'Wrapped BNB',
        address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
        decimals: 18,
        isNative: false,
        coinId: 7192
      },
      BUSD: {
        symbol: 'BUSD',
        name: 'BUSD Token',
        address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
        decimals: 18,
        isNative: false,
        coinId: 4687
      },
      USDT: {
        symbol: 'USDT',
        name: 'Tether USD',
        address: '0x55d398326f99059fF775485246999027B3197955',
        decimals: 18,
        isNative: false,
        coinId: 825
      },
      USDC: {
        symbol: 'USDC',
        name: 'USD Coin',
        address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
        decimals: 18,
        isNative: false,
        coinId: 3408
      },
      CAKE: {
        symbol: 'CAKE',
        name: 'PancakeSwap Token',
        address: '0x0E09FaBB73Bd3Ade0a17ECC321fD13a19e81cE82',
        decimals: 18,
        isNative: false,
        coinId: 7186
      },
      LINK: {
        symbol: 'LINK',
        name: 'ChainLink Token',
        address: '0xF8A0BF9cF54Bb92F17374d9e9A321E6a111a51bD',
        decimals: 18,
        isNative: false,
        coinId: 1975
      }
    }
  },
  bsc_testnet: {
    chainId: '0x61', // 97 in hex
    chainIdNumber: 97,
    chainName: 'BSC Testnet',
    nativeCurrency: {
      name: 'tBNB',
      symbol: 'tBNB',
      decimals: 18,
    },
    rpcUrls: ['https://data-seed-prebsc-1-s1.binance.org:8545'],
    blockExplorerUrls: ['https://testnet.bscscan.com'],
    routerAddress: '0x9Ac64Cc6e4415144C455BD8E4837Fea55603e5c3',
    factoryAddress: '0x6725F303b657a9451d8BA641348b6761A6CC7a17',
    wbnbAddress: '0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd',
    tokens: {
      BNB: {
        symbol: 'BNB',
        name: 'Binance Coin',
        address: 'BNB',
        decimals: 18,
        isNative: true,
        coinId: 1839
      },
      HUC: {
        symbol: 'HUC',
        name: 'HuanChain Token',
        address: '0x1A2B3C4D5E6F7A8B9C0D1E2F3A4B5C6D7E8F9A0B',
        decimals: 18,
        isNative: false,
        coinId: 99999
      },
      WBNB: {
        symbol: 'WBNB',
        name: 'Wrapped BNB',
        address: '0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd',
        decimals: 18,
        isNative: false,
        coinId: 7192
      },
      BUSD: {
        symbol: 'BUSD',
        name: 'BUSD Token',
        address: '0xeD24FC36d5Ee211Ea25A80239Fb8C4Cfd80f12Ee',
        decimals: 18,
        isNative: false,
        coinId: 4687
      },
      USDT: {
        symbol: 'USDT',
        name: 'Tether USD',
        address: '0x337610d27c682E347C9cD60BD4b3b107C9d34dDd',
        decimals: 18,
        isNative: false,
        coinId: 825
      }
    }
  },
  ethereum_mainnet: {
    chainId: '0x1', // 1 in hex
    chainIdNumber: 1,
    chainName: 'Ethereum',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    rpcUrls: ['https://mainnet.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161'],
    blockExplorerUrls: ['https://etherscan.io'],
    routerAddress: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D', // Uniswap V2 Router
    factoryAddress: '0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f', // Uniswap V2 Factory
    wbnbAddress: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', // WETH on Ethereum
    tokens: {
      ETH: {
        symbol: 'ETH',
        name: 'Ethereum',
        address: 'ETH',
        decimals: 18,
        isNative: true,
        coinId: 1027
      },
      HUC: {
        symbol: 'HUC',
        name: 'HuanChain Token',
        address: '0x38B0F25A12591b65bC992b8d0b2849F30689bA4F',
        decimals: 18,
        isNative: false,
        coinId: 99999
      },
      WETH: {
        symbol: 'WETH',
        name: 'Wrapped Ethereum',
        address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
        decimals: 18,
        isNative: false,
        coinId: 2396
      },
      USDC: {
        symbol: 'USDC',
        name: 'USD Coin',
        address: '0xA0b86a33E6441b8c4C8C0e4b8b8c8b8c8b8c8b8c',
        decimals: 6,
        isNative: false,
        coinId: 3408
      },
      USDT: {
        symbol: 'USDT',
        name: 'Tether USD',
        address: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
        decimals: 6,
        isNative: false,
        coinId: 825
      },
      DAI: {
        symbol: 'DAI',
        name: 'Dai Stablecoin',
        address: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
        decimals: 18,
        isNative: false,
        coinId: 4943
      },
      UNI: {
        symbol: 'UNI',
        name: 'Uniswap',
        address: '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984',
        decimals: 18,
        isNative: false,
        coinId: 7083
      },
      LINK: {
        symbol: 'LINK',
        name: 'ChainLink Token',
        address: '0x514910771AF9Ca656af840dff83E8264EcF986CA',
        decimals: 18,
        isNative: false,
        coinId: 1975
      }
    }
  },
  arbitrum_mainnet: {
    chainId: '0xa4b1', // 42161 in hex
    chainIdNumber: 42161,
    chainName: 'Arbitrum One',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    rpcUrls: ['https://arb1.arbitrum.io/rpc'],
    blockExplorerUrls: ['https://arbiscan.io'],
    routerAddress: '0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45',
    factoryAddress: '0x1F98431c8aD98523631AE4a59f267346ea31F984',
    wbnbAddress: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1', // WETH
    tokens: {
      ETH: {
        symbol: 'ETH',
        name: 'Ethereum',
        address: 'ETH',
        decimals: 18,
        isNative: true,
        coinId: 1027
      },
      HUC: {
        symbol: 'HUC',
        name: 'HuanChain Token',
        address: '0x38B0F25A12591b65bC992b8d0b2849F30689bA4F',
        decimals: 18,
        isNative: false,
        coinId: 99999
      },
      USDC: {
        symbol: 'USDC',
        name: 'USD Coin',
        address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
        decimals: 6,
        isNative: false,
        coinId: 3408
      },
      USDT: {
        symbol: 'USDT',
        name: 'Tether USD',
        address: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
        decimals: 6,
        isNative: false,
        coinId: 825
      },
      ARB: {
        symbol: 'ARB',
        name: 'Arbitrum',
        address: '0x912CE59144191C1204E64559FE8253a0e49E6548',
        decimals: 18,
        isNative: false,
        coinId: 11841
      }
    }
  },
  polygon_mainnet: {
    chainId: '0x89', // 137 in hex
    chainIdNumber: 137,
    chainName: 'Polygon PoS',
    nativeCurrency: {
      name: 'POL',
      symbol: 'POL',
      decimals: 18,
    },
    rpcUrls: ['https://polygon-rpc.com'],
    blockExplorerUrls: ['https://polygonscan.com'],
    routerAddress: '0xa5E0829CaCEd8fFDD4De3c43696c57F7D7A678ff', // QuickSwap Router
    factoryAddress: '0x5757371414417b8C6CAad45bAeF941aBc7d3Ab32',
    wbnbAddress: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270', // WPOL
    tokens: {
      POL: {
        symbol: 'POL',
        name: 'Polygon Token',
        address: 'POL',
        decimals: 18,
        isNative: true,
        coinId: 28321
      },
      HUC: {
        symbol: 'HUC',
        name: 'HuanChain Token',
        address: '0x38B0F25A12591b65bC992b8d0b2849F30689bA4F',
        decimals: 18,
        isNative: false,
        coinId: 99999
      },
      USDT: {
        symbol: 'USDT',
        name: 'Tether USD',
        address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
        decimals: 6,
        isNative: false,
        coinId: 825
      },
      USDC: {
        symbol: 'USDC',
        name: 'USD Coin',
        address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
        decimals: 6,
        isNative: false,
        coinId: 3408
      }
    }
  },
  ethereum_testnet: {
    chainId: '0x5', // 5 in hex (Goerli)
    chainIdNumber: 5,
    chainName: 'Ethereum Goerli Testnet',
    nativeCurrency: {
      name: 'GoerliETH',
      symbol: 'GoerliETH',
      decimals: 18,
    },
    rpcUrls: ['https://goerli.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161'],
    blockExplorerUrls: ['https://goerli.etherscan.io'],
    routerAddress: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D', // Uniswap V2 Router
    factoryAddress: '0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f', // Uniswap V2 Factory
    wbnbAddress: '0xB4FBF271143F4FBf7B91A5ded31805e42b2208d6', // WETH on Goerli
    tokens: {
      ETH: {
        symbol: 'ETH',
        name: 'Ethereum',
        address: 'ETH',
        decimals: 18,
        isNative: true,
        coinId: 1027
      },
      HUC: {
        symbol: 'HUC',
        name: 'HuanChain Token',
        address: '0x1A2B3C4D5E6F7A8B9C0D1E2F3A4B5C6D7E8F9A0B',
        decimals: 18,
        isNative: false,
        coinId: 99999
      },
      WETH: {
        symbol: 'WETH',
        name: 'Wrapped Ethereum',
        address: '0xB4FBF271143F4FBf7B91A5ded31805e42b2208d6',
        decimals: 18,
        isNative: false,
        coinId: 2396
      },
      USDC: {
        symbol: 'USDC',
        name: 'USD Coin',
        address: '0x07865c6E87B9F70255377e024ace6630C1Eaa37F',
        decimals: 6,
        isNative: false,
        coinId: 3408
      },
      USDT: {
        symbol: 'USDT',
        name: 'Tether USD',
        address: '0x509Ee0d083DdF8AC028f2a56731412edD63223B9',
        decimals: 6,
        isNative: false,
        coinId: 825
      },
      DAI: {
        symbol: 'DAI',
        name: 'Dai Stablecoin',
        address: '0x11fE4B6AE13d2a6055C8D9cF65c55bac32B5d844',
        decimals: 18,
        isNative: false,
        coinId: 4943
      }
    }
  }
};

function App() {
  const { t } = useLanguage();
  const [account, setAccount] = useState('');
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [balance, setBalance] = useState('0');
  const [currentTab, setCurrentTab] = useState('swap'); // 'swap' or 'liquidity'
  const [selectedNetwork, setSelectedNetwork] = useState('bsc_testnet'); // 'bsc_testnet', 'bsc_mainnet', 'ethereum_testnet', 'ethereum_mainnet'
  const [showApp, setShowApp] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const currentNetworkConfig = NETWORKS[selectedNetwork];

  useEffect(() => {
    checkConnection();
    
    // Listen for account changes
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      disconnectWallet();
    } else {
      checkConnection();
    }
  };

  const handleChainChanged = () => {
    checkConnection();
  };

  const checkConnection = async () => {
    if (window.ethereum) {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const accounts = await provider.listAccounts();
        const network = await provider.getNetwork();
        
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          setProvider(provider);
          setSigner(provider.getSigner());
          setChainId(network.chainId);
          
          // Auto-detect network based on chainId
          if (network.chainId === 8888) {
            setSelectedNetwork('huanchain_mainnet');
          } else if (network.chainId === 56) {
            setSelectedNetwork('bsc_mainnet');
          } else if (network.chainId === 97) {
            setSelectedNetwork('bsc_testnet');
          } else if (network.chainId === 42161) {
            setSelectedNetwork('arbitrum_mainnet');
          } else if (network.chainId === 137) {
            setSelectedNetwork('polygon_mainnet');
          } else if (network.chainId === 1) {
            setSelectedNetwork('ethereum_mainnet');
          } else if (network.chainId === 5) {
            setSelectedNetwork('ethereum_testnet');
          }
          
          const balance = await provider.getBalance(accounts[0]);
          setBalance(ethers.utils.formatEther(balance));
        }
      } catch (error) {
        console.error('Error checking connection:', error);
      }
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) {
      showToast(t('app.installMetaMask'), 'error');
      return;
    }

    try {
      // Request account access
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      
      // Switch to selected network
      await switchToNetwork(selectedNetwork);
      
      // Update connection state
      await checkConnection();
    } catch (error) {
      console.error('Error connecting wallet:', error);
    }
  };

  const switchToNetwork = async (networkKey) => {
    const networkConfig = NETWORKS[networkKey];
    
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: networkConfig.chainId }],
      });
    } catch (switchError) {
      // This error code indicates that the chain has not been added to MetaMask
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [networkConfig],
          });
        } catch (addError) {
          console.error(`Error adding ${networkConfig.chainName}:`, addError);
        }
      } else {
        console.error(`Error switching to ${networkConfig.chainName}:`, switchError);
      }
    }
  };

  const handleNetworkChange = async (networkKey) => {
    setSelectedNetwork(networkKey);
    if (account) {
      await switchToNetwork(networkKey);
      await checkConnection();
    }
  };

  const disconnectWallet = () => {
    setAccount('');
    setProvider(null);
    setSigner(null);
    setChainId(null);
    setBalance('0');
  };

  const isCorrectNetwork = chainId === currentNetworkConfig.chainIdNumber;

  return (
    <div className="App">
      {/* Decorative ambient background glows */}
      <div className="glow-sphere sphere-1"></div>
      <div className="glow-sphere sphere-2"></div>

      <Header 
        account={account}
        connectWallet={connectWallet}
        disconnectWallet={disconnectWallet}
        networkConfig={currentNetworkConfig}
        isCorrectNetwork={isCorrectNetwork}
        chainId={chainId}
        selectedNetwork={selectedNetwork}
        onNetworkChange={handleNetworkChange}
        currentTab={currentTab}
        onTabChange={(tab) => {
          if (tab === 'home') {
            setShowApp(false);
          } else {
            setCurrentTab(tab);
            setShowApp(true);
          }
        }}
        showApp={showApp}
        onShowAppChange={setShowApp}
      />

      <MarketTicker 
        onSelectToken={() => {
          setShowApp(true);
          setCurrentTab('swap');
        }}
      />

      <main className="main-content">

        {showApp || (account && isCorrectNetwork) ? (
          <div className="dapp-interface">
            {/* Sleek DApp Tab Switcher & Network Status */}
            <div className="dapp-nav-bar">
              <div className="dapp-tab-switcher">
                <button
                  className={`dapp-pill-btn ${currentTab === 'swap' ? 'active' : ''}`}
                  onClick={() => setCurrentTab('swap')}
                >
                  <span className="pill-icon"><NavSwapIcon size={16} /></span>
                  <span className="pill-label">{t('header.swap')}</span>
                </button>
                <button
                  className={`dapp-pill-btn ${currentTab === 'liquidity' ? 'active' : ''}`}
                  onClick={() => setCurrentTab('liquidity')}
                >
                  <span className="pill-icon"><NavLiquidityIcon size={16} /></span>
                  <span className="pill-label">{t('header.liquidity')}</span>
                </button>
                <button
                  className={`dapp-pill-btn ${currentTab === 'crosschain' ? 'active' : ''}`}
                  onClick={() => setCurrentTab('crosschain')}
                >
                  <span className="pill-icon"><NavBridgeIcon size={16} /></span>
                  <span className="pill-label">{t('header.bridge')}</span>
                </button>
                <button
                  className={`dapp-pill-btn ${currentTab === 'faucet' ? 'active' : ''}`}
                  onClick={() => setCurrentTab('faucet')}
                >
                  <span className="pill-icon"><NavFaucetIcon size={16} /></span>
                  <span className="pill-label">{t('header.faucet')}</span>
                </button>
              </div>

              <div className="dapp-network-indicator">
                <span className="network-live-dot"></span>
                <span className="network-live-name">{currentNetworkConfig.chainName}</span>
                <span className="network-live-tag">{t('dapp.live')}</span>
              </div>
            </div>

            <div className="tab-content">
              {currentTab === 'swap' ? (
                <Swap 
                  provider={provider} 
                  signer={signer} 
                  account={account} 
                  networkConfig={currentNetworkConfig}
                  connectWallet={connectWallet}
                />
              ) : currentTab === 'liquidity' ? (
                <Liquidity 
                  provider={provider} 
                  signer={signer} 
                  account={account} 
                  networkConfig={currentNetworkConfig}
                  connectWallet={connectWallet}
                />
              ) : currentTab === 'crosschain' ? (
                <Crosschain 
                  provider={provider} 
                  signer={signer} 
                  account={account} 
                  networkConfig={currentNetworkConfig}
                  connectWallet={connectWallet}
                />
              ) : (
                <Faucet 
                  provider={provider} 
                  signer={signer} 
                  account={account} 
                  networkConfig={currentNetworkConfig}
                  balance={balance}
                  connectWallet={connectWallet}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="welcome-section">
            <div className="hero-content">
              {/* Hero Header */}
              <div className="hero-header">
                <div className="hero-badge">
                  <span className="badge-icon"><RocketIcon size={14} color="#f3ba2f" /></span>
                  <span className="badge-text">{t('hero.badge')}</span>
                </div>
                <h1 className="hero-title">
                  <span className="title-main">{t('hero.titleMain')}</span>
                  <span className="title-subtitle">{t('hero.titleSub')}</span>
                </h1>
                <p className="hero-description">
                  {t('hero.desc', { network: currentNetworkConfig.chainName })}
                </p>
                <div className="hero-cta">
                  <button onClick={() => { setShowApp(true); setCurrentTab('swap'); }} className="cta-primary">
                    <span className="cta-icon"><RocketIcon size={18} /></span>
                    <span className="cta-text">{t('hero.launchApp')}</span>
                  </button>
                  {!account ? (
                    <button onClick={connectWallet} className="cta-secondary-launch">
                      <span className="cta-icon"><ZapIcon size={18} color="#f3ba2f" /></span>
                      <span className="cta-text">{t('hero.connectWallet')}</span>
                    </button>
                  ) : (
                    <button onClick={() => { setShowApp(true); setCurrentTab('liquidity'); }} className="cta-secondary-launch">
                      <span className="cta-icon"><DropsIcon size={18} color="#06b6d4" /></span>
                      <span className="cta-text">{t('hero.exploreLiquidity')}</span>
                    </button>
                  )}
                  <div className="cta-info">
                    <span>{t('hero.audited')}</span>
                  </div>
                </div>
              </div>

              {/* Statistics Section */}
              <div className="hero-stats">
                <div className="stats-container">
                  <div className="stat-card">
                    <div className="stat-icon"><ChartBarIcon size={24} color="#f3ba2f" /></div>
                    <div className="stat-content">
                      <div className="stat-value">$2.1B+</div>
                      <div className="stat-label">{t('stats.volume')}</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon"><UsersIcon size={24} color="#0152ea" /></div>
                    <div className="stat-content">
                      <div className="stat-value">150K+</div>
                      <div className="stat-label">{t('stats.users')}</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon"><CoinsIcon size={24} color="#10b981" /></div>
                    <div className="stat-content">
                      <div className="stat-value">500+</div>
                      <div className="stat-label">{t('stats.tokens')}</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon"><DropsIcon size={24} color="#06b6d4" /></div>
                    <div className="stat-content">
                      <div className="stat-value">$180M+</div>
                      <div className="stat-label">{t('stats.liquidity')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Features Grid */}
              <div className="features-section">
                <div className="section-header">
                  <h2 className="section-title">{t('features.title')}</h2>
                  <p className="section-subtitle">
                    {t('features.subtitle')}
                  </p>
                </div>
                <div className="features-grid">
                  <div className="feature-card premium">
                    <div className="feature-header">
                      <div className="feature-icon lightning">
                        <div className="icon-animation"><ZapIcon size={24} color="#f3ba2f" /></div>
                      </div>
                      <h3>{t('features.f1Title')}</h3>
                    </div>
                    <p>{t('features.f1Desc')}</p>
                    <div className="feature-stats">
                      <span>• {t('features.b1_1')}</span>
                      <span>• {t('features.b1_2')}</span>
                      <span>• {t('features.b1_3')}</span>
                    </div>
                  </div>
                  
                  <div className="feature-card premium">
                    <div className="feature-header">
                      <div className="feature-icon liquidity">
                        <div className="icon-animation"><DropsIcon size={24} color="#06b6d4" /></div>
                      </div>
                      <h3>{t('features.f2Title')}</h3>
                    </div>
                    <p>{t('features.f2Desc')}</p>
                    <div className="feature-stats">
                      <span>• {t('features.b2_1')}</span>
                      <span>• {t('features.b2_2')}</span>
                      <span>• {t('features.b2_3')}</span>
                    </div>
                  </div>
                  
                  <div className="feature-card premium">
                    <div className="feature-header">
                      <div className="feature-icon bridge">
                        <div className="icon-animation"><NavBridgeIcon size={24} color="#a855f7" /></div>
                      </div>
                      <h3>{t('features.f3Title')}</h3>
                    </div>
                    <p>{t('features.f3Desc')}</p>
                    <div className="feature-stats">
                      <span>• {t('features.b3_1')}</span>
                      <span>• {t('features.b3_2')}</span>
                      <span>• {t('features.b3_3')}</span>
                    </div>
                  </div>

                  <div className="feature-card premium">
                    <div className="feature-header">
                      <div className="feature-icon security">
                        <div className="icon-animation"><ShieldLockIcon size={24} color="#10b981" /></div>
                      </div>
                      <h3>{t('features.f4Title')}</h3>
                    </div>
                    <p>{t('features.f4Desc')}</p>
                    <div className="feature-stats">
                      <span>• {t('features.b4_1')}</span>
                      <span>• {t('features.b4_2')}</span>
                      <span>• {t('features.b4_3')}</span>
                    </div>
                  </div>

                  <div className="feature-card premium">
                    <div className="feature-header">
                      <div className="feature-icon analytics">
                        <div className="icon-animation"><TrendingUpIcon size={24} color="#3b82f6" /></div>
                      </div>
                      <h3>{t('features.f5Title')}</h3>
                    </div>
                    <p>{t('features.f5Desc')}</p>
                    <div className="feature-stats">
                      <span>• {t('features.b5_1')}</span>
                      <span>• {t('features.b5_2')}</span>
                      <span>• {t('features.b5_3')}</span>
                    </div>
                  </div>

                  <div className="feature-card premium">
                    <div className="feature-header">
                      <div className="feature-icon yield">
                        <div className="icon-animation"><CoinsIcon size={24} color="#f59e0b" /></div>
                      </div>
                      <h3>{t('features.f6Title')}</h3>
                    </div>
                    <p>{t('features.f6Desc')}</p>
                    <div className="feature-stats">
                      <span>• {t('features.b6_1')}</span>
                      <span>• {t('features.b6_2')}</span>
                      <span>• {t('features.b6_3')}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Getting Started Section */}
              <div className="getting-started-section">
                <div className="section-header">
                  <h2 className="section-title">{t('steps.title')}</h2>
                  <p className="section-subtitle">
                    {t('steps.subtitle')}
                  </p>
                </div>
                <div className="steps-container">
                  <div className="step-card">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h4>{t('steps.s1Title')}</h4>
                      <p>{t('steps.s1Desc')}</p>
                    </div>
                    <div className="step-icon"><WalletIcon size={24} color="#0152ea" /></div>
                  </div>
                  <div className="step-arrow"><ArrowRightIcon size={20} /></div>
                  
                  <div className="step-card">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h4>{t('steps.s2Title')}</h4>
                      <p>{t('steps.s2Desc')}</p>
                    </div>
                    <div className="step-icon"><CoinsIcon size={24} color="#10b981" /></div>
                  </div>
                  <div className="step-arrow"><ArrowRightIcon size={20} /></div>
                  
                  <div className="step-card">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h4>{t('steps.s3Title')}</h4>
                      <p>{t('steps.s3Desc')}</p>
                    </div>
                    <div className="step-icon"><RocketIcon size={24} color="#f59e0b" /></div>
                  </div>
                </div>
              </div>

              {/* Trusted By Section */}
              <div className="trusted-section">
                <div className="section-header">
                  <h3 className="section-title">{t('trusted.title')}</h3>
                </div>
                <div className="partners-grid">
                  <div className="partner-logo">PancakeSwap</div>
                  <div className="partner-logo">1inch</div>
                  <div className="partner-logo">Uniswap</div>
                  <div className="partner-logo">LayerZero</div>
                  <div className="partner-logo">Chainlink</div>
                  <div className="partner-logo">CertiK</div>
                </div>
              </div>

              {/* Final CTA Section */}
              {!account && (
                <div className="final-cta-section">
                  <div className="cta-card">
                    <h3>{t('cta.title')}</h3>
                    <p>{t('cta.desc')}</p>
                    <div className="cta-buttons">
                      <button onClick={connectWallet} className="cta-primary large">
                        <span className="cta-icon"><ZapIcon size={20} /></span>
                        <span className="cta-text">{t('hero.connectWallet')}</span>
                      </button>
                      <button onClick={() => { setShowApp(true); setCurrentTab('swap'); }} className="cta-secondary large">
                        <span className="cta-icon"><RocketIcon size={20} /></span>
                        <span className="cta-text">{t('hero.launchApp')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

          {/* Contract Info Section - Only show when connected */}
          {account && isCorrectNetwork && (
          <div className="contract-info-section">
            <div className="contract-info">
              <h3 style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ZapIcon size={16} color="var(--accent-color)" /> {t('contract.info')}
              </h3>
              <div className="contract-addresses">
                <div className="address-item">
                  <strong>{t('contract.router')}</strong> 
                  <span className="address">{currentNetworkConfig.routerAddress}</span>
                </div>
                <div className="address-item">
                  <strong>{t('contract.factory')}</strong> 
                  <span className="address">{currentNetworkConfig.factoryAddress}</span>
                </div>
              </div>
              <div className="wallet-details">
                <div className="detail-item">
                  <strong>{t('common.balance')}:</strong> {parseFloat(balance).toFixed(4)} {currentNetworkConfig.nativeCurrency.symbol}
                </div>
                <div className="detail-item">
                  <strong>{t('header.network')}:</strong> 
                  <span className={isCorrectNetwork ? 'correct-network' : 'wrong-network'} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {isCorrectNetwork ? (
                      <><CheckIcon size={14} color="#10b981" /> {currentNetworkConfig.chainName}</>
                    ) : (
                      <><ErrorIcon size={14} color="#ef4444" /> {t('header.wrongNetwork')}</>
                    )}
                  </span>
                </div>
              </div>
              {!isCorrectNetwork && (
                <button onClick={() => switchToNetwork(selectedNetwork)} className="switch-network-btn">
                  {t('contract.switchTo', { network: currentNetworkConfig.chainName })}
                </button>
              )}
            </div>
          </div>
        )}
      </main>

      <Footer selectedNetwork={selectedNetwork} networkConfig={currentNetworkConfig} />

      {/* Global Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default App; 